import channelsByLayer from "./channelsByLayer"
import {
  max,
  find,
  last,
  includes,
  reverse,
  sortBy,
  range,
  sum,
  flatten,
  capitalize,
} from "lodash"
import Lines from "~/components/lines"
import Neuron from "./neuron"
import { SelectMenu, Button, Spinner, IconButton, Dialog } from "evergreen-ui"

const ModuleTitle = ({
  children,
  subtitle,
  fontSize = 24,
  marginLeft = 10,
  ...props
}) => (
  <Surface marginY={2} flexFlow="row" {...props}>
    <Text
      fontSize={fontSize}
      opacity={0.7}
      fontWeight={600}
      marginLeft={marginLeft}
    >
      {children}
    </Text>
    {subtitle && (
      <Text
        marginLeft={5}
        fontSize={fontSize}
        opacity={0.4}
        fontWeight={600}
        marginLeft={10}
      >
        {subtitle}
      </Text>
    )}
  </Surface>
)

const allLayers = {
  inceptionv1: [
    "conv2d0",
    "conv2d1",
    "conv2d2",
    "mixed3a",
    "mixed3b",
    "mixed4a",
    "mixed4b",
    "mixed4c",
    "mixed4d",
    "mixed4e",
    "mixed5a",
    "mixed5b",
  ],
  alexnet: ["conv1_1", "conv2_1", "conv3_1"],
  inceptionv3: [
    "InceptionV3/InceptionV3/Conv2d_1a_3x3/Relu",
    "InceptionV3/InceptionV3/Conv2d_2a_3x3/Relu",
    "InceptionV3/InceptionV3/Conv2d_2b_3x3/Relu",
    "InceptionV3/InceptionV3/Conv2d_3b_1x1/Relu",
    "InceptionV3/InceptionV3/Conv2d_4a_3x3/Relu",
    "InceptionV3/InceptionV3/Mixed_5b/concat",
    "InceptionV3/InceptionV3/Mixed_5c/concat",
    "InceptionV3/InceptionV3/Mixed_5d/concat",
    "InceptionV3/InceptionV3/Mixed_6a/concat",
    "InceptionV3/InceptionV3/Mixed_6b/concat",
    "InceptionV3/InceptionV3/Mixed_6c/concat",
    "InceptionV3/InceptionV3/Mixed_6d/concat",
    "InceptionV3/InceptionV3/Mixed_6e/concat",
    "InceptionV3/InceptionV3/Mixed_7a/concat",
    "InceptionV3/InceptionV3/Mixed_7b/concat",
    "InceptionV3/InceptionV3/Mixed_7c/concat",
  ],
}

const moduleBg = `rgba(255, 255, 255,1)`

class DatasetExamples extends Component {
  render() {
    const { activeNeuron, modelName } = this.props
    const imageCount = 10
    // const activeNeuron = activeNeurons[0]
    const [layer, neuron] = activeNeuron.split(":")
    const neuronUrl = (neuron, layer, index) =>
      `https://storage.googleapis.com/clarity-public/nick/dataset_examples/${layer}/${neuron}/bin0/image${index}.jpg`

    return (
      <Surface
        position="absolute"
        zIndex={5000}
        marginLeft="auto"
        marginRight="auto"
        left="0"
        right="0"
        background="rgba(230, 230, 230, 1)"
        boxShadow="0 30px 60px -12px rgba(50,50,93,.04), 0 18px 36px -18px rgba(0,0,0,.06), 0 -12px 36px -8px rgba(0,0,0,.045)"
        style={{ border: `2px solid rgba(0, 0, 0, 0.2)` }}
        padding={8}
        paddingX={10}
        background={moduleBg}
        borderRadius={8}
        top={"105%"}
        width={500}
      >
        <ModuleTitle
          fontSize={16}
          subtitle={`${layer}:${neuron}`}
          marginLeft={5}
        >
          Neuron Details
        </ModuleTitle>

        <Surface alignItems="center" marginTop={5} flexFlow="row">
          <img
            src={neuronFeatureVis(modelName, layer, neuron)}
            width={130}
            style={{ borderRadius: 5 }}
          />
          <Surface
            flexFlow="row"
            marginLeft={20}
            flexWrap="wrap"
            width={322}
            borderRadius={5}
            style={{ border: "1px solid rgba(0, 0, 0, 0.2)" }}
            overflow="hidden"
          >
            {range(imageCount).map(index => (
              <img
                style={{ margin: 0, padding: 0 }}
                width={64}
                src={neuronUrl(neuron, layer, index)}
              />
            ))}
          </Surface>
        </Surface>
      </Surface>
    )
  }
}

const zeros = x => range(x).map(_ => 0)

export default class Circuit extends Component {
  state = { hovering: false }

  getGrad = (layer, neuron, layer2, neuron2) => {
    const { getWeight, modelName } = this.props
    const { weights } = this.props.data

    const forward =
      allLayers[modelName].indexOf(layer2) > allLayers[modelName].indexOf(layer)

    if (getWeight) {
      return forward
        ? getWeight(layer, neuron, layer2, neuron2)
        : getWeight(layer2, neuron2, layer, neuron)
    }

    const fromKey = `${layer}:${neuron}`
    const toKey = `${layer2}:${neuron2}`

    const key = forward ? `${fromKey}-${toKey}` : `${toKey}-${fromKey}`

    return weights[key]
  }

  getNextLayer = () => {
    const { modelName } = this.props
    const layers = allLayers[modelName]
    const { circuit } = this.props.data

    return layers[layers.indexOf(last(circuit).name) + 1]
  }

  getPrevLayer = () => {
    const { modelName } = this.props
    const layers = allLayers[modelName]
    const { circuit } = this.props.data

    return layers[layers.indexOf(circuit[0].name) - 1]
  }

  // neuron strs is a list of neurons like [3b:345, 3a:127]
  getGrads = (layer, neuron, neuronStrs) => {
    const xs = neuronStrs
      .map(neuronStr => {
        const [layer2, neuron2] = neuronStr.split(":")
        if (layer2 === layer) {
          return null
        }
        const weights = this.getGrad(layer, +neuron, layer2, +neuron2)
        return weights
      })
      .filter(i => i !== null)

    if (xs.length === 0) {
      return null
    }

    const totals = zeros(xs[0].grad.length).map(_ => zeros(xs[0].grad.length))

    xs.forEach(weights => {
      weights.grad.forEach((row, rowIndex) => {
        row.forEach((col, colIndex) => {
          totals[rowIndex][colIndex] += col // Math.max(0, col)
        })
      })
    })

    return { total: sum(xs.map(i => i.total)), grad: totals }
  }

  layerHasNeuron = (layerName, neuron) => {
    const { circuit } = this.props.data
    const layer = circuit[circuit.map(({ name }) => name).indexOf(layerName)]

    for (const mod of layer.modules) {
      if (includes(mod.neurons, neuron)) {
        return true
      }
    }

    return false
  }

  layerVariance = (layerName, activeNeurons) => {
    const { modelName } = this.props

    const neuronTotals = range(channelsByLayer[modelName][layerName]).map(
      neuron => {
        const { total } = this.getGrads(layerName, neuron, activeNeurons)
        return total
      },
    )

    return (
      sum(
        neuronTotals.filter((total, neuron) =>
          this.layerHasNeuron(layerName, neuron),
        ),
      ) / sum(neuronTotals)
    )
  }

  onSetNeurons = activeNeurons => {
    const maxScales = {}
    const { circuit } = this.props.data

    activeNeurons.forEach(activeNeuron => {
      const [layer, neuron] = activeNeuron.split(":")
      circuit.forEach(({ name, modules }) => {
        if (name === layer) {
          return
        }

        if (!maxScales[name]) {
          maxScales[name] = 0
        }

        maxScales[name] +=
          max(
            flatten(
              modules.map(({ neurons }) =>
                neurons.map(neuron2 => {
                  const value = this.getGrad(layer, neuron, name, neuron2)
                  if (!value) return 0

                  return max(flatten(value.grad))
                }),
              ),
            ),
          ) || 0
      })
    })

    if (this.props.onChangeState) {
      this.props.onChangeState({ activeNeurons, maxScales })
    } else {
      this.setState({ activeNeurons, maxScales })
    }
  }

  getActiveState = () => {
    return (
      this.props.circuitState || {
        activeNeurons: this.state.activeNeurons,
        maxScales: this.state.maxScales,
        recommendModal: null,
      }
    )
  }

  getMaxScales = () => {
    return this.props.maxScales || this.state.maxScales
  }

  layerShorthand = layer => {
    const { modelName } = this.props
    if (modelName === "inceptionv1") {
      return layer.slice(5)
    }

    return layer
  }

  hasDataset = () => {
    const { modelName, hasDataset = true } = this.props
    return modelName === "inceptionv1" && hasDataset
  }

  componentWillMount() {
    const { circuitState, firstActiveNeurons } = this.props

    if (circuitState && circuitState.activeNeurons !== null) return null

    const getFirstNeuron = () => {
      const { circuit } = this.props.data

      for (const layer of circuit) {
        for (const group of layer.modules) {
          if (group.neurons.length > 0) {
            return `${layer.name}:${group.neurons[0]}`
          }
        }
      }
    }

    this.onSetNeurons(firstActiveNeurons || [getFirstNeuron()])
  }

  render() {
    const { circuit } = this.props.data
    const { hovering } = this.state

    const {
      showLines = true,
      inlineLayerNames = false,
      circuitScale = 1,
      size = 60,
      moduleTitleSize = 16,
      neuronsPerModule = Infinity,
      isDesigner = false,
      onAddGroup,
      onAddNeuron,
      onAddLayerBefore,
      onAddLayerAfter,
      modelName,
    } = this.props

    const { activeNeurons, maxScales, recommendModal } = this.getActiveState()

    if (!activeNeurons) {
      return <Spinner />
    }

    const activeLayer = activeNeurons[0].split(":")[0]

    const paths = {}

    circuit.forEach(({ name: layerName, modules }) => {
      modules.forEach(({ name: moduleName }) => {
        circuit.forEach(({ name: layer2Name, modules }) => {
          if (
            allLayers[modelName].indexOf(layerName) -
              allLayers[modelName].indexOf(layer2Name) ===
            1
          ) {
            const fromKey = `${layerName}:${moduleName}`
            paths[fromKey] = {}
            modules.forEach(({ name: module2Name }) => {
              const toKey = `${layer2Name}:${module2Name}`
              paths[fromKey][toKey] = 0.2
            })
          }
        })
      })
    })

    const allPaths = [paths]

    const Module = ({ children, noBody, ...props }) => {
      return (
        <Surface
          padding={3}
          background={moduleBg}
          borderRadius={8}
          boxShadow="0 30px 60px -12px rgba(50,50,93,.04), 0 18px 36px -18px rgba(0,0,0,.06), 0 -12px 36px -8px rgba(0,0,0,.025)"
          style={{ border: `2px solid rgba(0, 0, 0, 0.2)` }}
          flexFlow="row"
          flexWrap="wrap"
          justifyContent="space-around"
          alignItems="center"
          {...props}
        >
          {children}
        </Surface>
      )
    }

    const moduleWidth =
      this.props.moduleWidth ||
      {
        4: 300,
        5: 250,
      }[circuit.length] ||
      440

    const modulePaddingY = moduleCount =>
      ({
        1: 20,
        2: 20,
        3: 15,
        4: 10,
      }[moduleCount] || 5)

    const keyMap = {
      DELETE_NODE: ["del", "backspace"],
    }

    const handlers = {
      DELETE_NODE: () => {
        // this.onDelete()
      },
    }

    return (
      <Lines allPaths={showLines ? allPaths : []}>
        <Surface
          flexFlow="row"
          alignItems="center"
          justifyContent="center"
          alignSelf="center"
          marginTop={10}
        >
          {isDesigner && (
            <Button
              iconBefore="plus"
              height={24}
              onClick={() => {
                onAddLayerBefore(this.getPrevLayer())
                this.onSetNeurons(activeNeurons)
              }}
            >
              {this.layerShorthand(this.getPrevLayer())}
            </Button>
          )}
          {circuit.map(({ name, modules }) => {
            const layer = name

            const recommendedNeurons = (layer, count) => {
              const totals = range(channelsByLayer[modelName][layer]).map(
                neuron => {
                  if (this.layerHasNeuron(layer, neuron)) {
                    return { neuron, total: -1 }
                  }

                  return {
                    neuron,
                    total: this.getGrads(layer, neuron, activeNeurons).total,
                  }
                },
              )

              const neurons = reverse(sortBy(totals, "total"))
                .slice(0, count)
                .map(({ neuron }) => neuron)

              return neurons
            }

            const recommendPanel = (count, isBig = false) => (
              <div key={`${name}:rec`}>
                <Surface marginTop={20}>
                  <Surface
                    flexFlow="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {!isBig && (
                      <ModuleTitle fontSize={moduleTitleSize}>
                        Similar Neurons
                      </ModuleTitle>
                    )}
                    <Surface marginY={5}>
                      <div style={{ display: "flex", flexFlow: "row" }}>
                        <SelectMenu
                          options={modules
                            .map(({ name }) => name)
                            .map(label => ({ label, value: label }))}
                          selected={
                            this.state[`activeRec:${name}`] ||
                            (modules.length > 0 ? modules[0].name : "New Group")
                          }
                          onSelect={({ value }) =>
                            this.setState({
                              [`activeRec:${name}`]: value,
                            })
                          }
                        >
                          <Button height={24} iconBefore="plus">
                            {this.state[`activeRec:${name}`] ||
                              (modules.length > 0
                                ? modules[0].name
                                : "New Group")}
                          </Button>
                        </SelectMenu>
                        {!isBig && (
                          <IconButton
                            marginLeft={5}
                            height={24}
                            onClick={() => {
                              this.props.onChangeState({
                                recommendModal: name,
                              })
                            }}
                            icon="fullscreen"
                          />
                        )}
                      </div>
                    </Surface>
                  </Surface>
                  <Surface data-line-id={name + ":rec"}>
                    <Module width={isBig ? 750 : moduleWidth}>
                      {recommendedNeurons(name, count).map(neuron => (
                        <Neuron
                          size={size}
                          neuron={neuron}
                          modelName={modelName}
                          obj={this.getGrads(layer, neuron, activeNeurons)}
                          key={neuron}
                          onMouseEnter={() => {
                            this.setState({
                              hovering: name + ":" + neuron,
                            })
                          }}
                          onMouseLeave={() => {
                            this.setState({ hovering: false })
                          }}
                          onMouseDown={() =>
                            onAddNeuron(
                              name,
                              this.state[`activeRec:${name}`] ||
                                modules[0].name,
                              neuron,
                            )
                          }
                          maxValue={
                            maxScales[name] || 40 * activeNeurons.length
                          }
                          sameLayer={name === activeLayer}
                          isActive={find(
                            activeNeurons,
                            activeStr => activeStr === `${layer}:${neuron}`,
                          )}
                          layer={name}
                        />
                      ))}
                    </Module>
                  </Surface>
                </Surface>
              </div>
            )

            const marginX = this.props.moduleMarginX || 30 * circuitScale

            return (
              <Surface marginX={marginX}>
                {!inlineLayerNames && (
                  <Surface
                    flexFlow="row"
                    borderBottom
                    paddingBottom={10}
                    justifyContent="space-between"
                  >
                    <Surface flexFlow="row">
                      <Text
                        fontSize={20}
                        opacity={0.6}
                        fontWeight={600}
                        marginLeft={10}
                      >
                        Layer {this.layerShorthand(name)}
                      </Text>

                      {false && name !== activeLayer && (
                        <Text
                          fontSize={20}
                          opacity={0.4}
                          fontWeight={600}
                          marginLeft={10}
                        >
                          Variance{" "}
                          {Math.round(
                            this.layerVariance(name, activeNeurons).toFixed(2) *
                              100,
                          )}
                          %
                        </Text>
                      )}
                    </Surface>
                    {isDesigner && (
                      <div style={{ display: "flex", alignSelf: "center" }}>
                        <Button
                          height={24}
                          iconBefore="plus"
                          onClick={() => {
                            const groupName = prompt("Group name?")
                            onAddGroup(name, groupName)
                          }}
                        >
                          Group
                        </Button>
                      </div>
                    )}
                  </Surface>
                )}
                {modules.map(({ name: moduleName, neurons }) => {
                  return (
                    <div key={`${name}:${moduleName}`}>
                      <Surface marginY={modulePaddingY(modules.length)}>
                        <ModuleTitle
                          onClick={() =>
                            this.onSetNeurons(
                              neurons.map(neuron => `${layer}:${neuron}`),
                            )
                          }
                          fontSize={moduleTitleSize}
                          subtitle={
                            inlineLayerNames && this.layerShorthand(layer)
                          }
                        >
                          {capitalize(moduleName)}
                        </ModuleTitle>
                        <Surface data-line-id={name + ":" + moduleName}>
                          <Module width={moduleWidth * circuitScale}>
                            {neurons.slice(0, neuronsPerModule).map(neuron => (
                              <Neuron
                                size={size * circuitScale}
                                neuron={neuron}
                                modelName={modelName}
                                obj={this.getGrads(
                                  layer,
                                  neuron,
                                  activeNeurons,
                                )}
                                key={neuron}
                                onMouseEnter={() => {
                                  console.log(
                                    "entering neuron",
                                    name + ":" + neuron,
                                  )
                                  this.setState({
                                    hovering: name + ":" + neuron,
                                  })
                                }}
                                onMouseLeave={() => {
                                  this.setState({ hovering: false })
                                }}
                                onMouseDown={() => {
                                  this.onSetNeurons([name + ":" + neuron])
                                }}
                                maxValue={
                                  maxScales[name] || 40 * activeNeurons.length
                                }
                                sameLayer={name === activeLayer}
                                isActive={find(
                                  activeNeurons,
                                  activeStr =>
                                    activeStr === `${layer}:${neuron}`,
                                )}
                                layer={name}
                              />
                            ))}
                          </Module>
                        </Surface>
                      </Surface>
                    </div>
                  )
                })}

                {isDesigner && name !== activeLayer && recommendPanel(9)}

                {recommendModal === name && (
                  <Dialog
                    width={800}
                    isShown={true}
                    onCloseComplete={() =>
                      this.props.onChangeState({ recommendModal: null })
                    }
                    hasHeader={false}
                    hasFooter={false}
                  >
                    {recommendModal &&
                      name !== activeLayer &&
                      recommendPanel(80, true)}
                  </Dialog>
                )}
              </Surface>
            )
          })}
          {isDesigner && (
            <Button
              height={24}
              iconAfter="plus"
              onClick={() => {
                onAddLayerAfter(this.getNextLayer())
              }}
            >
              {this.layerShorthand(this.getNextLayer())}
            </Button>
          )}
        </Surface>
        {hovering && this.hasDataset() && (
          <DatasetExamples modelName={modelName} activeNeuron={hovering} />
        )}
      </Lines>
    )
  }
}
